import { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import logo from '../../Assets/Logo/Sridhar_Dental-logo.png';

// Slugify function - must match Contact.jsx
const slugify = (city) =>
  city.toLowerCase().replace(/[()]/g, '').replace(/\s+/g, '-');

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();
  
  const mobileMenuRef = useRef(null);
  const location = useLocation();
  
  const [treatmentsOpen, setTreatmentsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const toggleButtonRef = useRef(null);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
    setTreatmentsOpen(false);
  }, [location.pathname]);

  // Scroll handler with throttle
  useEffect(() => {
    let timeoutId;
    const handleScroll = () => {
      if (timeoutId) return;
      timeoutId = setTimeout(() => {
        setIsScrolled(window.scrollY > 40);
        timeoutId = null;
      }, 80);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  // Close mobile menu on outside click / ESC
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        isOpen &&
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(e.target) &&
        toggleButtonRef.current &&
        !toggleButtonRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };

    const handleEsc = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEsc);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen]);

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const navLinkClass = ({ isActive }) => `
    relative font-medium transition-all duration-300
    ${isActive 
      ? 'text-blue-600 font-semibold after:w-full' 
      : 'text-gray-800 hover:text-blue-600 after:w-0 hover:after:w-full'}
    after:absolute after:left-0 after:-bottom-1 after:h-0.5 after:bg-blue-600
    after:transition-all after:duration-300 after:ease-out
  `;

  const mobileNavClass = ({ isActive }) => `
    block py-3 px-4 rounded-lg transition
    ${isActive 
      ? 'bg-blue-50 text-blue-700 font-semibold' 
      : 'text-gray-800 hover:bg-gray-50 active:bg-gray-100'}
  `;

  // Branch data for the top bar
  const branchInfo = [
    {
      city: "VIJAYAWADA",
      address: "Vijaya Talkies Centre, Eluru Rd, Governor Peta, Vijayawada, Andhra Pradesh 520002",
      phone: "+91 9490 948 081",
      email: "dr.sridhardental@gmail.com",
      hours: "Mon-Sat: 10:00 AM – 8:00 PM | Sun: 10:00 AM – 1:00 PM"
    },
    {
      city: "KPHB HYDERABAD",
      address: "MIG 70, 2nd Floor, Above ICICI Bank, KPHB, Hyderabad 500072",
      phone: "+91 8121 222 888",
      email: "jntu@sridhardental.com",
      hours: "Mon-Sat: 10:00 AM – 2:00 PM, 4:30 PM – 8:30 PM | Sun: 10:00 AM – 2:00 PM"
    },
    {
      city: "AMEERPET",
      address: "1st floor, above darlies ice cream, Ameerpet, Hyderabad 500082",
      phone: "+91 8121 666 111",
      email: "ameerpet@sridhardental.com",
      hours: "Mon-Sat: 10:00 AM – 9:00 PM | Sun: 10:00 AM – 2:00 PM"
    },
    {
      city: "NALLAGANDLA (BHEL)",
      address: "1st Floor, VMS Arcade, above Apollo Pharmacy, Nallagandla, Hyderabad 500073",
      phone: "+91 954 202 7700",
      email: "nallagandla@sridhardental.com",
      hours: "Mon-Sat: 10:00 AM – 2:00 PM, 4:30 PM – 8:30 PM | Sun: 10:00 AM – 2:00 PM, 4:30 PM – 7:00 PM"
    }
  ];

  // Handler for clicking on a specific branch
  const handleBranchClick = (e, city) => {
    e.stopPropagation();
    navigate(`/contact#${slugify(city)}`);
  };

  // Handler for clicking on empty space in top bar
  const handleTopBarClick = () => {
    navigate('/contact');
  };

  return (
    <>
      {/* Sticky Container - Both bars together */}
      <div className="sticky top-0 z-[60]">
        {/* Top Bar */}
        <div 
          onClick={handleTopBarClick}
          className="bg-gradient-to-r from-blue-600 to-blue-700 text-white text-[10px] sm:text-xs py-2 sm:py-2.5 border-b border-blue-500 cursor-pointer hover:from-blue-700 hover:to-blue-800 transition-all duration-300"
        >
          <div className="container mx-auto px-3 sm:px-6">
            <div className="flex items-center justify-center flex-wrap gap-x-2 sm:gap-x-4 gap-y-1">
              {branchInfo.map((branch, index) => (
                <span 
                  key={index} 
                  className="inline-flex items-center cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={(e) => handleBranchClick(e, branch.city)}
                >
                  <span className="font-semibold text-[9px] sm:text-[11px] tracking-wide">{branch.city}</span>
                  {index < branchInfo.length - 1 && (
                    <span className="mx-1 sm:mx-2 text-blue-300 text-[9px] sm:text-[11px]">|</span>
                  )}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Main Header */}
        <header
          className={`w-full transition-all duration-500 ease-out
            ${isScrolled 
              ? 'bg-white/95 backdrop-blur-md shadow-lg' 
              : 'bg-white/95 backdrop-blur-sm'}
            ${prefersReducedMotion ? '!transition-none' : ''}`}
        >
          <div className="container mx-auto px-4 sm:px-5 lg:px-8 py-3 sm:py-4 flex justify-between items-center">
            {/* Logo */}
            <Link
              to="/"
              className="flex-shrink-0"
              onClick={(e) => {
                if (location.pathname === "/") {
                  e.preventDefault();
                }
                document.documentElement.scrollTop = 0;
                document.body.scrollTop = 0;
                window.scrollTo(0, 0);
              }}
            >
              <img 
                src={logo} 
                alt="Sridhar Dental Clinic Logo" 
                className="h-9 sm:h-11 md:h-14 object-contain" 
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8 xl:gap-10"> 
              <NavLink to="/" className={navLinkClass} end>Home</NavLink>
              <NavLink to="/about" className={navLinkClass}>About Us</NavLink>
              <NavLink to="/services" className={navLinkClass}>Services</NavLink>
              <NavLink to="/blog" className={navLinkClass}>Our Blog</NavLink>
              <NavLink to="/gallery" className={navLinkClass}>Gallery</NavLink>
              <NavLink to="/contact" className={navLinkClass}>Contact</NavLink>
            </nav>

            {/* CTA + Mobile toggle */}
            <div className="flex items-center gap-3 sm:gap-4">
              <Link
                to="/book-appointment"
                className="hidden md:inline-flex items-center bg-blue-600 hover:bg-blue-700 text-white px-4 sm:px-6 py-2 sm:py-2.5 rounded-full font-medium text-sm sm:text-base transition shadow-sm hover:shadow-md"
              >
                Book Appointment
              </Link>

              <button
                ref={toggleButtonRef}
                type="button"
                className="lg:hidden text-gray-800 p-1 -mr-1 sm:-mr-2"
                onClick={() => setIsOpen(prev => !prev)}
                aria-expanded={isOpen}
                aria-label="Toggle navigation menu"
              >
                <svg className="w-8 h-8 sm:w-9 sm:h-9" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.2}
                    d={isOpen ? 'M6 18L18 6M6 6l12 12' : 'M4 8h16M4 16h16'}
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Mobile menu */}
          <div
            ref={mobileMenuRef}
            className={`lg:hidden bg-white border-t transition-all duration-300 ease-out overflow-hidden
              ${isOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'}`}
          >
            <nav className="container mx-auto px-4 sm:px-5 py-4 sm:py-6 flex flex-col gap-2 sm:gap-3 text-sm sm:text-base">
              <NavLink to="/" className={mobileNavClass} end onClick={() => setIsOpen(false)}>Home</NavLink>
              <NavLink to="/about" className={mobileNavClass} onClick={() => setIsOpen(false)}>About Us</NavLink>
              <NavLink to="/services" className={mobileNavClass} onClick={() => setIsOpen(false)}>Services</NavLink>
              <NavLink to="/blog" className={mobileNavClass} onClick={() => setIsOpen(false)}>Our Blog</NavLink>
              <NavLink to="/gallery" className={mobileNavClass} onClick={() => setIsOpen(false)}>Gallery</NavLink>
              <NavLink to="/contact" className={mobileNavClass} onClick={() => setIsOpen(false)}>Contact</NavLink>

              <Link
                to="/book-appointment"
                className="mt-3 sm:mt-4 bg-blue-600 hover:bg-blue-700 text-white py-3 sm:py-3.5 px-6 rounded-xl font-medium text-center shadow-sm"
                onClick={() => setIsOpen(false)}
              >
                Book Appointment
              </Link>
            </nav>
          </div>
        </header>
      </div>
    </>
  );
};

export default Header;